/**
 * D231: topics — the keyed answer table a thing speaks from when it is ASKED
 * about something (`docs/design/actors.md` §4, the half of conversation that
 * needs no actor; `actors-content.md` §5.1 split it out as round B1).
 *
 * @remarks
 * `askNarratives` (R1.1, D137 for rooms) is the ONE-topic case: what is said
 * when the player asks about THIS thing, whoever they ask. A topic table is
 * the general shape — what this thing says when asked about ANY subject it
 * knows — and it is what lets EURYDICE, an ambient item, answer `ask the
 * voice about the film` without a flag per subject: her table is keyed by
 * subject, and each entry decides for itself when it holds and whether it
 * is said once.
 *
 * The table's discipline is the reaction table's (D27) and the `use`
 * interaction's: `keys` are matched against the `about` phrase — a plain
 * word or phrase AS THE PLAYER TYPES IT (folded as input is), or an item id,
 * which the engine expands to every word that item answers to — in declared
 * order, and the FIRST topic whose keys match, whose `when` holds and which
 * is not already spent speaks. Its `narratives` are ordinary lines (any
 * source; conditional text works inside them) and its `effects` fold exactly
 * as a `use` interaction's do, so a question can change the world.
 *
 * `once` is Mike's ruling 4 (2026-10-04): what is said stays said. A `once`
 * topic is spent in PLAYER scope — the ledger is `narrative.spentTopicIds`,
 * kept across a checkpoint restore like the voice ledger (D146) — because a
 * line the speaker can un-say is an asymmetry the player can catch
 * (`actors-content.md` §5.3). There is no `scope` field: nothing here is the
 * world's.
 *
 * Why keys and not a word per topic: one subject has many names, and the
 * player types whichever comes to mind. An item-id key says "every word the
 * mask answers to" in one token; plain keys carry the subjects no item
 * stands for (`directive`, `steps`). A key that contains an underscore is
 * an id reference and must name a declared item (the loader refuses one
 * that does not); words have no underscores.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { effectSchema } from './effects.js';
import { topicIdSchema } from './identifiers.js';
import { nonBlankStringSchema } from './primitives.js';
import { narrativeLinesSchema } from './transcript.js';

/**
 * One topic — see the module remarks.
 */
export const topicDefinitionSchema = z
	.strictObject({
		/** Effects folded after the narratives, in authored order — a `use` interaction's fold. Default none: an answer need not change anything. */
		effects: z.array(effectSchema).readonly().default([]),
		/**
		 * What the subject may be called for this topic to answer: a word or
		 * phrase as the player types it, or an item id (any key containing an
		 * underscore), which stands for every word that item answers to.
		 */
		keys: z.array(nonBlankStringSchema).min(1).readonly(),
		/** What is said — min-1, as every narratives array: a topic that says nothing is not a topic. */
		narratives: narrativeLinesSchema,
		/** Spent once spoken, in player scope (Mike's ruling 4). Default false: a topic answers every time it is asked. */
		once: z.boolean().default(false),
		topicId: topicIdSchema,
		/** Speaks only while this holds. Absent, always — the fallback arm of a key shared by several topics. */
		when: conditionSchema.optional(),
	})
	.readonly();

/** One authored topic — see {@link topicDefinitionSchema}. */
export type TopicDefinition = z.infer<typeof topicDefinitionSchema>;

/**
 * A thing's topic table: declared order is match order. `min(1)`: an empty
 * table is `topicFallbackNarratives` with nothing to fall back from, which
 * the loader would otherwise have to explain.
 */
export const topicsSchema = z.array(topicDefinitionSchema).min(1).readonly();

/** A thing's topics — see {@link topicsSchema}. */
export type Topics = z.infer<typeof topicsSchema>;

/**
 * Is this topic key an ID REFERENCE (to be expanded to the named item's
 * words) rather than a word the player types? The one rule, stated once,
 * read by the loader (which refuses an id nothing declares) and the engine
 * (which expands it): words have no underscores; content ids are
 * snake_case.
 *
 * @param key - One key of a topic
 * @returns Whether the key names an item by id
 */
export const isTopicIdKey = (key: string): boolean => key.includes('_');
